// The journey ribbon: one element that replaces the stepper, the left-rail
// step list and the Back button.
//
// The insight it's built on is that "go back to step 3" is not a thing anyone
// actually wants to do. What they want is to *change an answer*. So instead of
// showing a row of step names to click, this shows the answers themselves —
// "Single trip", "Europe", "12–20 Mar" — and clicking one edits it in place,
// without leaving the page you're on.
//
// That makes one strip do four jobs at once:
//   · progress      — how many answers you've given, and what's left
//   · orientation   — what you told us, always visible, in your own words
//   · navigation    — jump to any earlier point by editing it
//   · correction    — fix a typo without losing the step you're on
//
// It's the pattern behind Google Flights' filter chips and the newer UAE/UK
// motor aggregators, and it scales better than a stepper: a twelve-step funnel
// produces a readable trail, where twelve step-name pills produce a scrollbar.
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Chip,
  ClickAwayListener,
  Fade,
  Paper,
  Popper,
  Stack,
  Typography,
} from "@mui/material";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import OpenInFullRoundedIcon from "@mui/icons-material/OpenInFull";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { findStepByPath } from "../../config/journeys";
import { recordAnswers } from "../../redux/actions";
import { getDataAdapter } from "../../config/dataAdapters";
import { SchemaForm, useSchemaForm } from "../../form";
import { useActionError } from "../elements/ActionError";
import { summaryFieldsOf, summariseStep } from "./trailSummary";
import RibbonAction from "./RibbonAction";

const GOLD = "#C9A777";
const INK = "#3d322b";
const MUTED = "#7B6E66";

/**
 * Does this choice field render illustrated cards?
 *
 * Two shapes reach here. Travel lists its options inline, each carrying its own
 * `image`. Motor resolves them from a reference domain and attaches the artwork
 * in a `decorate` map keyed by code — so an Array.isArray check alone misses
 * every Motor step, which is exactly the one that overflows worst.
 *
 * Gated on `props.illustrated` because that is what ChoiceInput itself requires
 * before it renders artwork at all; an image on an option it never draws is not
 * a reason to widen the panel.
 *
 * @param {{ options?: *, props?: * }} field
 */
const hasArtwork = (field) => {
  if (!field.props?.illustrated) return false;
  const opts = field.options;
  if (Array.isArray(opts)) return opts.some((o) => o && o.image);
  const decorate = opts && opts.decorate;
  return !!decorate && Object.values(decorate).some((o) => o && o.image);
};

/**
 * @param {Object} [props]
 * @param {Object} [props.sx]
 * @param {Object} [props.action]
 *   The step's forward action, shown compactly at the ribbon's right edge while
 *   the in-flow footer is off screen: { label, onClick, busy, disabled,
 *   progress, nextIncomplete }. Omit for steps with no forward action.
 * @param {boolean} [props.showAction]  Whether the compact action is visible now.
 */
const AnswerTrail = ({ sx, action, showAction = false } = {}) => {
  const location = useLocation();
  const dispatch = useDispatch();
  const insuranceType = useSelector(
    (/** @type {import("../../redux/types").RootState} */ s) => s.insuranceType
  );
  const answers = useSelector(
    (/** @type {import("../../redux/types").RootState} */ s) => s.journeyAnswers
  );

  const match = useMemo(
    () => findStepByPath(location.pathname, insuranceType),
    [location.pathname, insuranceType]
  );

  const [editing, setEditing] = useState(/** @type {number | null} */ (null));

  if (!match) return null;

  const { journey, index: activeIndex } = match;
  const steps = journey.steps;
  const given = answers[journey.key] || {};
  const progress = ((activeIndex + 0.5) / steps.length) * 100;

  // Only steps already answered become chips. Steps ahead stay out of the way
  // entirely — showing eight greyed-out future labels is the clutter the old
  // stepper created and told the customer nothing useful.
  const trail = steps
    .slice(0, activeIndex)
    .map((step, idx) => ({ step, idx, text: summariseStep(step, given) }))
    .filter((entry) => entry.text);

  return (
    <Box
      component="nav"
      aria-label={`${journey.label} progress`}
      sx={{
        position: "sticky",
        top: { xs: 80, md: 100 },
        zIndex: 1050,
        // Opaque, not frosted. At 0.88 the form scrolled *through* the ribbon:
        // headings and card edges stayed legible behind the chips, which reads
        // as a rendering fault rather than as depth.
        bgcolor: "#FBF1E2",
        borderBottom: "1px solid rgba(201,167,119,0.18)",
        // Paint the band between the top of the viewport and this ribbon.
        // While stuck, nothing else occupies it: the navbar is ~84px against a
        // 100px offset, and it drops back to position:absolute and scrolls away
        // entirely whenever isAppBarFixed is stale. Either way the form was
        // visible above the ribbon. Owning the strip makes that impossible
        // without depending on the navbar being in the right state.
        "&::before": {
          content: '""',
          position: "absolute",
          left: 0,
          right: 0,
          bottom: "100%",
          height: { xs: 80, md: 100 },
          bgcolor: "#FBF1E2",
          pointerEvents: "none",
        },
        ...sx,
      }}
    >
      <Box sx={{ maxWidth: 1280, mx: "auto", px: { xs: 2, md: 4 }, pt: 1.25, pb: 0.25 }}>
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{
            overflowX: "auto",
            pb: 1.25,
            "&::-webkit-scrollbar": { display: "none" },
            scrollbarWidth: "none",
          }}
        >
          {trail.map(({ step, idx, text }) => (
            <AnswerChip
              key={step.key}
              step={step}
              text={text}
              journey={journey}
              values={given}
              open={editing === idx}
              onOpen={() => setEditing(idx)}
              onClose={() => setEditing(null)}
              onSaved={(patch) => {
                dispatch(recordAnswers(journey.key, patch));
                setEditing(null);
              }}
            />
          ))}

          {/* Where you are now. Not a link — you're already here. */}
          <Chip
            label={steps[activeIndex].label}
            aria-current="step"
            sx={{
              flexShrink: 0,
              height: { xs: 38, tablet: 30 },
              fontWeight: 700,
              fontSize: "0.8125rem",
              color: "#fff",
              bgcolor: GOLD,
              border: "1px solid",
              borderColor: GOLD,
              boxShadow: "0 4px 12px rgba(201,167,119,0.32)",
              "& .MuiChip-label": { px: 1.5 },
            }}
          />

          <Box sx={{ flex: 1, minWidth: 8 }} />

          <Typography
            sx={{
              flexShrink: 0,
              color: MUTED,
              fontSize: "0.75rem",
              fontWeight: 600,
              letterSpacing: 0.3,
              whiteSpace: "nowrap",
              display: { xs: "none", sm: "block" },
            }}
          >
            {activeIndex + 1} of {steps.length}
          </Typography>

          {action && <RibbonAction show={showAction} {...action} />}
        </Stack>
      </Box>

      {/* Hairline progress, flush to the bottom edge. */}
      <Box
        role="progressbar"
        aria-valuenow={Math.round(progress)}
        aria-valuemin={0}
        aria-valuemax={100}
        sx={{ position: "relative", height: 2, bgcolor: "rgba(201,167,119,0.18)" }}
      >
        <Box
          sx={{
            position: "absolute",
            left: 0,
            top: 0,
            height: "100%",
            width: `${progress}%`,
            background: `linear-gradient(90deg, #d3b48a, ${GOLD})`,
            transition: "width .45s cubic-bezier(.2,.8,.2,1)",
          }}
        />
      </Box>
    </Box>
  );
};

// One answered step, shown as its answer. Clicking opens a small editor for
// just the fields that answer summarises — a quick correction, in place. The
// full step is one click further, for the rare case someone wants to redo all
// of it.
const AnswerChip = ({ step, text, journey, values, open, onOpen, onClose, onSaved }) => {
  const anchorRef = useRef(null);
  const navigate = useNavigate();
  const caseId = useSelector(
    (/** @type {import("../../redux/types").RootState} */ s) => s.caseId
  );
  const [saving, setSaving] = useState(false);
  const { error, report, clear } = useActionError("We couldn't save that change. Please try again.");

  // A schema containing only the summarised fields, so the popover stays a
  // popover — the car-details step has 25 fields and none of them belong in a
  // dropdown.
  const miniSchema = useMemo(() => {
    const names = summaryFieldsOf(step);
    const fields = (step.form?.sections || [])
      .flatMap((s) => s.fields || [])
      .filter((f) => names.includes(f.name))
      .map((f) => {
        if (f.type !== "choice") return { ...f, col: 12 };
        // Options carrying artwork go two to a row. Stacked, a pair of
        // 566/748 portrait cards is taller than the viewport and the panel
        // runs off the bottom of the window; side by side they both fit.
        // Plain choices stay one per row, which reads better in a narrow
        // column and costs no height worth reclaiming.
        return {
          ...f,
          col: 12,
          props: { ...(f.props || {}), cardCols: hasArtwork(f) ? 6 : 12 },
        };
      });
    return { id: `${step.key}-quick`, sections: [{ fields }] };
  }, [step]);

  // Two cards need room. At 360px they get 160 each and every label wraps to
  // three lines, which is why this panel stacked them in the first place.
  const wide = useMemo(
    () => miniSchema.sections[0].fields.some((f) => f.type === "choice" && hasArtwork(f)),
    [miniSchema]
  );

  const form = useSchemaForm({ schema: miniSchema, initial: values });

  // A failure from a previous attempt must not greet the next one.
  useEffect(() => {
    if (open) clear();
  }, [open, clear]);

  const apply = async () => {
    if (!form.validateAll()) return;
    const patch = form.payload();
    setSaving(true);
    try {
      const adapter = getDataAdapter(journey.key);
      if (adapter && caseId) await adapter.save(caseId, patch);
      onSaved(patch);
    } catch (err) {
      // Keep the popover open on failure. Closing it would look like the edit
      // had been accepted, and the chip would still show the old answer with
      // no explanation.
      report(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Chip
        ref={anchorRef}
        label={text}
        onClick={onOpen}
        deleteIcon={<EditRoundedIcon sx={{ fontSize: 14 }} />}
        onDelete={onOpen}
        icon={<CheckRoundedIcon sx={{ fontSize: 15 }} />}
        sx={{
          flexShrink: 0,
          height: { xs: 38, tablet: 30 },
          maxWidth: { xs: 180, tablet: 220 },
          cursor: "pointer",
          fontWeight: 600,
          fontSize: "0.8125rem",
          color: INK,
          bgcolor: "#fff",
          border: "1px solid rgba(201,167,119,0.38)",
          transition: "all .18s ease",
          "& .MuiChip-icon": { color: GOLD, ml: 0.75 },
          "& .MuiChip-deleteIcon": { color: "rgba(123,110,102,0.5)" },
          "&:hover": {
            bgcolor: "#fffaf2",
            borderColor: GOLD,
            boxShadow: "0 3px 10px rgba(201,167,119,0.20)",
            "& .MuiChip-deleteIcon": { color: GOLD },
          },
        }}
      />

      <Popper
        open={open}
        anchorEl={anchorRef.current}
        placement="bottom-start"
        transition
        sx={{ zIndex: 1300 }}
      >
        {({ TransitionProps }) => (
          <Fade {...TransitionProps} timeout={140}>
            <Box>
              <ClickAwayListener onClickAway={onClose}>
                <Paper
                  elevation={0}
                  sx={{
                    mt: 1,
                    p: 2,
                    // Never wider than the screen: a phone is 375px, and the
                    // wide variant would otherwise hang off both edges.
                    width: {
                      xs: "calc(100vw - 32px)",
                      sm: wide ? 520 : 360,
                    },
                    maxWidth: wide ? 520 : 360,
                    borderRadius: 3,
                    border: "1px solid rgba(201,167,119,0.28)",
                    boxShadow: "0 18px 40px rgba(123,110,102,0.18)",
                  }}
                >
                  <Typography
                    variant="overline"
                    sx={{
                      display: "block",
                      color: "#a1885e",
                      fontWeight: 700,
                      letterSpacing: 1.4,
                      fontSize: "0.68rem",
                      lineHeight: 1.6,
                    }}
                  >
                    {step.label}
                  </Typography>

                  <SchemaForm schema={miniSchema} form={form} disabled={saving} />

                  {error && (
                    <Typography
                      role="alert"
                      sx={{ mt: 1, color: "#d4575a", fontSize: "0.8125rem", lineHeight: 1.45 }}
                    >
                      {error}
                    </Typography>
                  )}

                  <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                    <Button
                      onClick={apply}
                      disabled={saving || !form.isValid}
                      sx={{
                        flex: 1,
                        textTransform: "none",
                        fontWeight: 600,
                        borderRadius: 999,
                        color: "#fff",
                        background: `linear-gradient(135deg, #d3b48a, ${GOLD})`,
                        "&.Mui-disabled": {
                          background: "rgba(123,110,102,0.16)",
                          color: "rgba(123,110,102,0.55)",
                        },
                      }}
                    >
                      {saving ? "Saving…" : "Update"}
                    </Button>
                    <Button
                      onClick={() => {
                        onClose();
                        navigate(step.path);
                      }}
                      startIcon={<OpenInFullRoundedIcon sx={{ fontSize: 15 }} />}
                      sx={{
                        textTransform: "none",
                        fontWeight: 600,
                        borderRadius: 999,
                        color: MUTED,
                        "&:hover": { bgcolor: "rgba(123,110,102,0.06)" },
                      }}
                    >
                      All
                    </Button>
                  </Stack>
                </Paper>
              </ClickAwayListener>
            </Box>
          </Fade>
        )}
      </Popper>
    </>
  );
};

export default AnswerTrail;
